/**
 * The pull gesture, and the reload it performs.
 *
 * The arithmetic and the state machine are in `domain/pullToRefresh.ts` so they can be tested in
 * node. What is here is the three listeners, the indicator, and the reload itself — and the
 * reload is the part worth reading, because "refresh" in this app does not mean what it usually
 * means.
 *
 * NINE MANAGERS HAVE NO RELOAD BUTTON. They were told to add this to their home screen, which
 * makes it a standalone window with no address bar and no native pull gesture. The service worker
 * only swaps to a new build on a real page load, so without this there is no way for them to pick
 * up a deploy at all — which is exactly what happened on 25 August, twice.
 */

import { useEffect, useRef, useState } from 'react';
import {
  IDLE, MAX_PULL, THRESHOLD, type PullState, begin, canStart, ink, label, move, release,
} from '../domain/pullToRefresh.js';
import { LOGO_URL } from './logo.js';

/** Whether the focused element is something somebody is typing into. */
function typingNow(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
}

/** Whether the touch began inside something that scrolls on its own — a board, a wide table. */
function inScroller(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    const s = getComputedStyle(el);
    const scrolls = /(auto|scroll)/.test(s.overflowX + s.overflowY);
    if (scrolls && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Ask the worker for a new build, then reload into it.
 *
 * The update check is the whole point and it is deliberately given a short leash: on a phone with
 * no signal `update()` can hang, and a pull that appears to do nothing is worse than one that
 * reloads the build it already had. Two seconds, then reload regardless.
 */
async function reload(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await Promise.race([
        reg.update(),
        new Promise((done) => { setTimeout(done, 2000); }),
      ]);
    }
  } catch { /* An update check that fails is not a reason not to reload. */ }
  window.location.reload();
}

/**
 * Whether the touch began on something that owns its own drag, or inside a dialog.
 *
 * `touch-action: none` is the declaration an element makes when it intends to handle pointer
 * movement itself (the photo crop stage); a dialog or sheet is a surface over the page, and a
 * gesture inside it is about the sheet. Either way the page underneath must not move.
 */
function owned(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    if (el.getAttribute('role') === 'dialog' || el.classList.contains('kdsheet')) return true;
    if (getComputedStyle(el).touchAction === 'none') return true;
    el = el.parentElement;
  }
  return false;
}

export function PullToRefresh() {
  const [state, setState] = useState<PullState>(IDLE);
  const live = useRef<PullState>(IDLE);
  const startX = useRef(0);

  useEffect(() => {
    const set = (s: PullState) => { live.current = s; setState(s); };

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      if (!canStart({
        scrollY: window.scrollY,
        typing: typingNow(),
        inScroller: inScroller(e.target),
        owned: owned(e.target),
        phase: live.current.phase,
      })) return;
      startX.current = t.clientX;
      set(begin(t.clientY));
    };

    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || live.current.phase === 'idle' || live.current.phase === 'refreshing') return;
      /*
       * If the page has scrolled under the finger the gesture was a scroll all along. Checking
       * here as well as at the start is what stops a pull arming after a flick back to the top.
       */
      if (window.scrollY > 0) { set(IDLE); return; }
      const next = move(live.current, t.clientY, t.clientX - startX.current);
      /*
       * `preventDefault` only once this is definitely a pull, and only while the listener is
       * non-passive — otherwise every ordinary scroll on the page pays for this feature.
       */
      if (next.distance > 0 && e.cancelable) e.preventDefault();
      set(next);
    };

    const onEnd = () => {
      const next = release(live.current);
      set(next);
      if (next.phase === 'refreshing') void reload();
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  /*
   * THE PAGE MOVES, NOT A BANNER OVER IT. The first version drew a fixed strip that slid down on
   * top of the header, which reads as a notification rather than as a gesture — Tim's word for it
   * was clunky, and he was right: nothing you are touching responds to your finger.
   *
   * So the shift is published as a custom property and the CONTENT carries it. `.chrome` and
   * `main` translate together; `.tabbar` is their sibling and stays put, which is what you want —
   * the nav is chrome, not content. The indicator is then simply revealed in the gap that opens
   * above, the way every native implementation of this does it.
   *
   * A custom property rather than inline styles because the two elements that move are not this
   * component's to render, and reaching across the tree with refs to set transforms on them would
   * be a much worse coupling than one variable the stylesheet reads.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--ptr-shift', `${Math.round(state.distance)}px`);
    /* Only while the finger is down. On release the transition class does the springing back. */
    root.classList.toggle('ptr-settling', state.phase === 'idle' || state.phase === 'refreshing');
    return () => { root.style.removeProperty('--ptr-shift'); };
  }, [state.distance, state.phase]);

  if (state.phase === 'idle') return null;

  /*
   * TWO RAMPS, NOT ONE. `pct` is progress towards arming, and it is what the ring winds round and
   * what the centre fills to — it has to complete exactly where the gesture arms, or the dial is
   * lying about the threshold.
   *
   * `shown` is how visible the indicator is, and it deliberately runs three times faster. When
   * both came off `pct` and the threshold was 72, the indicator was solid almost at once. At 259
   * the same code would leave it a quarter opaque through the first hundred pixels of a
   * three-hundred-and-eighty pixel drag — which is the complaint that caused this gesture to be
   * rebuilt in the first place: nothing you are touching appears to respond. It is legible within
   * the first 90px now, and what changes over the rest of the pull is the dial and the colour.
   */
  const armed = state.phase === 'armed' || state.phase === 'refreshing';
  const pct = Math.min(state.distance / THRESHOLD, 1);
  const shown = Math.min(state.distance / (THRESHOLD * 0.35), 1);

  /*
   * `color` on the container, and everything inside it — ring, fill, text — is `currentColor`.
   * One value to set per frame instead of four, and the parts cannot drift apart mid-gesture.
   */
  /*
   * IT GROWS INTO THE GAP IT IS GIVEN. At 39px the dial and the label stand about 130px tall, and
   * the indicator's box is only as tall as the pull so far — so for the first inch of the gesture
   * the thing simply did not fit, and `overflow: hidden` cropped the dial at the top and the word
   * at the bottom. Scaling by the fraction of that 130px the gap has opened keeps it whole the
   * whole way down: content height stays at 0.85 of the distance, so it can never be clipped, and
   * what the eye sees is the indicator arriving rather than being cut out of the dark.
   */
  const grow = Math.min(state.distance / NEED, 1);
  return (
    <div className={`ptr ${state.phase}`} aria-live="polite"
      style={{ height: `${Math.min(state.distance, MAX_PULL)}px`, color: ink(pct, armed) }}>
      <div className="ptrinner" style={{ transform: `scale(${grow.toFixed(3)})` }}>
        {/*
          THE CREST COMES OUT OF THE DOT. Mounted only in the `refreshing` phase, so its one-shot
          animation runs once and then leaves with the indicator.

          It is a sibling of the dial inside a wrapper the exact size of the dial, which is what
          makes "starts hidden behind the dot" true rather than approximately true: at scale 1 it
          is 54px in the same place as the 54px ring, at zero opacity. Everything it then does is
          one transform away from there.
        */}
        <span className="ptrdialwrap">
          {state.phase === 'refreshing' && (
            <span className="ptrlogo" aria-hidden="true"
              style={{ backgroundImage: `url(${LOGO_URL})` }} />
          )}
          <Dial pct={pct} shown={shown} spinning={state.phase === 'refreshing'} />
        </span>
        <span className="ptrlabel">
          <span className="ptrtext" style={{ opacity: shown }}>{label(state.phase)}</span>
        </span>
      </div>
    </div>
  );
}

/** The indicator's natural height at full size — 54px dial, 14px gap, one 39px line. */
const NEED = 130;

/**
 * The dial: a ring that winds round to the threshold, and a centre that fills as it gets there.
 *
 * SVG rather than the bordered div this used to be. A CSS border can be rotated and that was
 * enough when the whole indicator was 18px, but it cannot draw a partial arc and it cannot fill
 * from the middle — and both of those are the ask: *"fill the circle as it gets close to armed"*.
 * A `stroke-dashoffset` gives the arc exactly, and a concentric disc with a growing radius gives
 * the fill without a second element or a mask.
 */
function Dial({ pct, shown, spinning }: { pct: number; shown: number; spinning: boolean }) {
  const R = 42;
  const C = 2 * Math.PI * R;
  /*
   * THE FILL STARTS AT 55%, not at zero. A centre that grows from the first pixel reads as a
   * blob that happens to be getting bigger; one that stays empty and then floods in over the last
   * stretch reads as arriving. It reaches full exactly with the ring.
   */
  const fill = Math.min(Math.max((pct - 0.55) / 0.45, 0), 1);
  return (
    <svg className={`ptrdial ${spinning ? 'spin' : ''}`} viewBox="0 0 100 100"
      style={{ opacity: shown }} aria-hidden="true">
      <circle className="ptrtrack" cx="50" cy="50" r={R} />
      <circle className="ptrarc" cx="50" cy="50" r={R}
        strokeDasharray={C}
        /* Spinning, the arc is a fixed quarter — a complete ring going round says nothing. */
        strokeDashoffset={spinning ? C * 0.75 : C * (1 - pct)}
        transform="rotate(-90 50 50)" />
      <circle className="ptrfill" cx="50" cy="50" r={R * 0.66 * fill} />
    </svg>
  );
}
